import styled from 'styled-components';
import _ from 'lodash';
import { memo, useEffect, useState } from 'react';
import { FaPause, FaPlay, FaRegEye } from 'react-icons/fa';

import { APP_IMG, NEXT_PUBLIC_DEFAULT_COUNT_VIEW } from 'src/constants';
import { sendDataLayerGlobal } from 'src/utils/datalayer/send-datalayer';
import { convertBadge, numeralFormat, timeAgo } from 'src/utils/helper';
import useResponsive from 'src/utils/devices';

const CardV = ({
  color = '',
  className = '',
  data,
  showTitle = true,
  showTimeAgo = false,
  showReadmore = true,
  showCountView = true,
  showFooter = true,
  showBadge = true,
  eventDataLayer = {},
  target = '',
  path = '',
  pause = false
}) => {
  if (_.isEmpty(data)) return null;
  const [countView, setCountView] = useState(0);
  const responsive = useResponsive();
  const conditionCountview = showCountView && countView > Number(NEXT_PUBLIC_DEFAULT_COUNT_VIEW) && countView > 500 && responsive.isDesktop;
  const conditionBadge = showBadge && convertBadge(data, false);
  useEffect(() => {
    setCountView(data?.pageviews || 0);
  }, [data]);

  return (
    <CardWrapper className={`${className} card-h`} color={color}>
      <a
        href={data?.link || path}
        aria-label={data?.title}
        target={`${target === 'blank' ? '_blank' : '_self'}`}
        rel={`${target === 'blank' ? 'noopener noreferrer nofollow' : ''}`}
        title={data?.title}
        tabIndex={-1}
        className='card-h-href'
        // ! DATALAYER
        onClick={() => sendDataLayerGlobal({ ...eventDataLayer })}
      >
        {/* ---------------------------------- IMAGE ---------------------------------  */}
        <CardImage className='card-image'>
          <picture className='resolution-image'>
            <img src={data?.image ? `${APP_IMG}${data?.image}?x-image-process=style/md` : `${APP_IMG}/default.jpg`} width='100%' height='100%' loading='lazy' />
          </picture>
          {!!conditionBadge && (
            <div className='badge'>
              <small>{conditionBadge}</small>
            </div>
          )}
          {data.is_video === true && <div className={`play-btn ${pause ? '--pause' : ''}`}>{pause ? <FaPause size={14} /> : <FaPlay size={14} />}</div>}
        </CardImage>
        <div className='card-detail'>
          {/* ---------------------------------- CONTENT -------------------------------  */}
          <div className='card-content'>{showTitle && <h3 className='title line-clamp --2'>{data?.title}</h3>}</div>

          {/* ------------------------------ CONTENT-FOOTER ----------------------------  */}
          {showFooter && (
            <div className='card-footer'>
              <div className='footer-left'>
                {showTimeAgo && (
                  <div className='time-ago'>
                    <small> {timeAgo(data?.published_at)}</small>
                  </div>
                )}
                {conditionCountview && (
                  <div className='count-view'>
                    <FaRegEye size={12} />
                    <small>{numeralFormat(Number(countView))}</small>
                  </div>
                )}
              </div>
              <div className='footer-right'>{showReadmore && <small className='video-readmore'>อ่านต่อ...</small>}</div>
            </div>
          )}
        </div>
      </a>
    </CardWrapper>
  );
};

const CardImage = styled.div`
  position: relative;
  overflow: hidden;
  background: #f1f2f3;
  border-radius: 10px 10px 0 0;

  .resolution-image {
    display: flex;
    position: relative;
    padding-top: 56.25%;
    img {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      object-fit: cover;

      transform-origin: 50% 65%;
      transition: transform 2s, filter 0.5s ease-out;
    }
    /* @media (max-width: 1024px) {
      padding-top: 85%;
    }
    @media (max-width: 767px) {
      padding-top: 56.25%;
    }
    @media (max-width: 380px) {
      padding-top: 80%;
    } */
  }
`;

const CardWrapper = styled.div`
  cursor: pointer;
  position: relative;
  overflow: hidden;
  width: 100%;
  min-width: auto;
  height: auto;
  display: flex;
  flex-direction: column;
  &.card-h-href {
    display: grid;
    grid-template-columns: 1fr;
    grid-template-rows: 2fr 1fr;
    grid-template-areas:
      'card-image card-image'
      'card-detail card-detail';
    .card-image {
      grid-area: card-image;
    }
    .card-detail {
      grid-area: card-detail;
      display: flex;
      flex-direction: column;
      justify-content: space-around;
    }
  }
  .card-detail {
    background: #fff;
    /* padding: 10px; */
    border-radius: 0 0 10px 10px;
    padding: 5px;
    /* @media (max-width: 769px) {
    } */
  }
  .badge {
    position: absolute;
    bottom: 5px;
    left: 5px;
    background: ${({ color }) => color || 'var(--primary-color)'};
    padding: 2px 10px;
    border-radius: 5px;
    @media (max-width: 1300px) and (min-width: 769px) {
      display: none;
    }
    small {
      font-size: 14px;
      line-height: 1.45;
      font-weight: 500;
      color: #fff;
    }
  }
  .play-btn {
    background: ${({ color }) => color || 'var(--primary-color)'};
    position: absolute;
    bottom: 0%;
    left: 0%;
    transform: translate(0%, 0%);
    padding: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 0 3px 0 0;
    &.--pause {
      background: #838383;
    }
    svg {
      color: #fff;
    }
  }
  /* ---------------------------------- HOVER : CARD --------------------------------- */
  &:hover {
    .card-image {
      img {
        transform: scale(1.1);
      }
    }
    .card-content {
      .title {
        color: #939393;
      }
    }
  }

  .card-content {
    padding: 0;

    .title {
      position: relative;
      min-height: 35px;
      background-position: 0;
      font-size: 18px;
      line-height: 1.45;
      font-weight: 300;
      color: #111;
      margin: 0px;
      text-decoration: unset;
      transition: all 0.3s ease-in-out;
      text-align: start;
      @media (max-width: 1023px) {
        /* font-size: 16px; */
        margin: 12px;
      }
      @media (max-width: 768px) {
        margin: 8px;
        font-size: 16px;
      }
      @media (max-width: 550px) {
        min-height: 30px;
        margin: 2px 2px 5px 2px;
        /* font-size: 14px; */
      }
      @media (max-width: 375px) {
        margin: 0;
        /* font-size: 12px; */
      }
    }
    .line-clamp {
      display: -webkit-box;
      -webkit-box-orient: vertical;
      overflow: hidden;
      -webkit-line-clamp: 2;
    }
  }
  .card-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: none;
    border: none;
    padding: 0;
    /* padding: 5px 0 0 0; */

    .footer-left {
      display: flex;
      align-items: center;

      .time-ago,
      .count-view {
        display: flex;
        align-items: center;
        font-size: 14px;
        line-height: 1.45;
        font-weight: 300;
        color: #939393;
        padding-right: 8px;
        svg {
          margin-right: 5px;
        }
        @media (max-width: 769px) {
          font-size: 12px;
        }
      }
    }

    .footer-right {
      display: flex;
      align-items: center;
      justify-content: space-between;
      @media (max-width: 1300px) and (min-width: 1024px) {
        display: none;
      }
      @media (max-width: 768px) {
        display: none;
      }
      .video-readmore {
        font-size: 12px;
        line-height: 1.45;
        font-weight: 400;
        color: ${({ color }) => color || 'var(--primary-color)'};

        padding-right: 8px;
        transition: all 0.3s ease-in-out;
        /* ---------------------------------- HOVER : VIEW_MORE --------------------------------- */
        &:hover {
          color: ${({ color }) => color || 'var(--primary-color)'};
        }
      }
    }
  }
`;
export default memo(CardV);
